import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAuth, getRedirectResult } from 'firebase/auth';

import { app as firebaseApp } from 'src/firebase-client';
import { LINKEDIN_FLOW_STORAGE_KEY } from './linkedin-flow';

import { signIn } from './sign-in';

export const checkRedirectSignIn = createAsyncThunk<boolean, undefined, { rejectValue: string }>(
  'user/checkSignInRedirect',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const user = await getUser();

      if (user) {
        if (user.provider === 'google') {
          await dispatch(signIn({ provider: 'google', idToken: await user.getIdToken(true) })).unwrap();
        } else {
          await dispatch(signIn({ provider: 'linkedin', state: user.state, verifier: user.verifier })).unwrap();
        }

        return true;
      }
    } catch (e) {
      return rejectWithValue((e as Error).message);
    }

    return false;
  }
);

async function getUser(): Promise<
  | { provider: 'linkedin'; state: string; verifier: string }
  | { provider: 'google'; getIdToken: (forceRefresh?: boolean) => Promise<string> }
  | null
> {
  if (new URLSearchParams(window.location.search).get('provider') === 'linkedin') {
    const state = new URLSearchParams(window.location.search).get('state');
    const savedFlow = window.sessionStorage.getItem(LINKEDIN_FLOW_STORAGE_KEY);
    window.sessionStorage.removeItem(LINKEDIN_FLOW_STORAGE_KEY);

    if (!state || !savedFlow) throw new Error('LinkedInStateMissing');

    const flow = JSON.parse(savedFlow) as { state: string; verifier: string };
    if (flow.state !== state || !/^[a-f0-9]{64}$/.test(flow.verifier)) throw new Error('LinkedInStateMismatch');

    return { provider: 'linkedin', state, verifier: flow.verifier };
  }

  const firebaseAuth = getAuth(firebaseApp);
  const redirectResult = await getRedirectResult(firebaseAuth);

  if (redirectResult?.user) {
    return { provider: 'google', getIdToken: (forceRefresh) => redirectResult.user.getIdToken(forceRefresh) };
  }

  return null;
}
